import { Clock, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Video } from "@/types/video";
import { cn } from "@/lib/utils";
import { getCategoryBadgeClass, getCategoryLabel, normalizeCategoryList } from "@/lib/category-config";

interface VideoCardProps {
  video: Video;
  onClick?: () => void;
  showCategoryBadges?: boolean;
}

export function VideoCard({ video, onClick, showCategoryBadges = true }: VideoCardProps) {
  const categories = normalizeCategoryList([
    ...(Array.isArray(video.categories) ? video.categories : []),
    video.dbCategory,
  ]);

  const [thumbnailSrc, setThumbnailSrc] = useState(video.thumbnailUrl);
  const [isGeneratingThumb, setIsGeneratingThumb] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const isVideoUrl =
    video.thumbnailUrl?.includes(".mp4") ||
    video.thumbnailUrl?.includes(".webm") ||
    video.thumbnailUrl?.includes(".mov");

  useEffect(() => {
    if (!isVideoUrl || !videoRef.current || !canvasRef.current) return;

    const targetVideo = videoRef.current;
    const targetCanvas = canvasRef.current;
    setIsGeneratingThumb(true);

    const handleLoadedData = () => {
      targetVideo.currentTime = 1;
    };

    const handleSeeked = () => {
      const ctx = targetCanvas.getContext("2d");
      if (!ctx) return;

      targetCanvas.width = targetVideo.videoWidth;
      targetCanvas.height = targetVideo.videoHeight;
      ctx.drawImage(targetVideo, 0, 0);

      const dataUrl = targetCanvas.toDataURL("image/jpeg", 0.8);
      setThumbnailSrc(dataUrl);
      setIsGeneratingThumb(false);
    };

    targetVideo.addEventListener("loadeddata", handleLoadedData);
    targetVideo.addEventListener("seeked", handleSeeked);

    return () => {
      targetVideo.removeEventListener("loadeddata", handleLoadedData);
      targetVideo.removeEventListener("seeked", handleSeeked);
    };
  }, [isVideoUrl]);

  return (
    <article
      onClick={onClick}
      className={cn(
        "group cursor-pointer overflow-hidden rounded-2xl border border-border/50 bg-card transition-all duration-300",
        "shadow-card hover:-translate-y-1 hover:shadow-card-hover",
      )}
    >
      {isVideoUrl ? (
        <>
          <video
            ref={videoRef}
            src={video.thumbnailUrl.split("#")[0]}
            className="hidden"
            muted
            playsInline
            crossOrigin="anonymous"
            preload="metadata"
          />
          <canvas ref={canvasRef} className="hidden" />
        </>
      ) : null}

      <div className="relative aspect-[16/9] overflow-hidden bg-muted">
        {isGeneratingThumb ? (
          <div className="flex h-full w-full items-center justify-center bg-muted">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <img
            src={thumbnailSrc || "/placeholder.svg"}
            alt={video.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setThumbnailSrc("/placeholder.svg")}
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" />

        {video.duration ? (
          <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-foreground/80 px-2 py-1 text-xs font-medium text-primary-foreground backdrop-blur-sm">
            <Clock className="h-3 w-3" />
            {video.duration}
          </span>
        ) : null}

        {video.isLocked ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-foreground/50 backdrop-blur-[3px]">
            <Lock className="h-8 w-8 text-primary-foreground" />
            <span className="text-xs font-medium text-primary-foreground">会员专享</span>
          </div>
        ) : null}
      </div>

      <div className="space-y-3 p-4">
        <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-card-foreground">
          {video.title}
        </h3>

        {showCategoryBadges ? (
          <div className="flex flex-wrap gap-1.5">
            {categories.length > 0 ? (
              categories.map((category) => (
                <span
                  key={category}
                  className={cn("rounded-full px-2 py-0.5 text-xs font-medium", getCategoryBadgeClass(category))}
                >
                  {getCategoryLabel(category, true)}
                </span>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">未分类</span>
            )}
          </div>
        ) : null}
      </div>
    </article>
  );
}
